import React, { useCallback, useEffect, useMemo } from 'react';
import { func, string } from 'prop-types';

import { NotificationManager } from '@atlas/core';
import { BasePopup, HeaderTitle, Icon } from '@atlas/ui';

import { SIGN_UP_POPUP_STORAGE_KEY } from 'common/constants';
import { showMultiPopup } from 'common/Notifications/Popups/MultiPopup/MultiPopupManager';
import useBonuses from 'hooks/useBonuses';
import useLanguage from 'src/hooks/useLanguage';

import BonusCardWrapper from './BonusCardWrapper';
import BonusesWrapper from './BonusesWrapper';

const propTypes = {
  id: string.isRequired,
  onClose: func,
};

const RegistrationBonusPopup = ({ id, onClose = () => {} }) => {
  const { activateBonus, bonuses = [], isLoading, wallet } = useBonuses();
  const { alpha3Code } = useLanguage();

  const filteredBonuses = useMemo(
    () =>
      bonuses
        .map(bonus => {
          const bonusImage = bonus?.localizedImageInfoList?.find(
            item => item.language.toLowerCase() === alpha3Code.toLowerCase(),
          );

          return {
            ...bonus,
            imageDescription: bonusImage?.imageDescription,
            imageLink: bonusImage?.imageLink,
          };
        })
        .filter(bonus => bonus.imageDescription || bonus.imageLink),
    [bonuses, alpha3Code],
  );

  const closePopupHandler = useCallback(() => {
    NotificationManager.removeNotification(id);
    showMultiPopup('sign_up', SIGN_UP_POPUP_STORAGE_KEY);
    onClose();
  }, [id, onClose]);

  const activateBonusHandler = useCallback(
    async bonus => {
      closePopupHandler();
      await activateBonus(bonus);
    },
    [activateBonus, closePopupHandler],
  );

  useEffect(() => {
    if (!isLoading && filteredBonuses.length === 0) {
      closePopupHandler();
    }
  }, [isLoading, filteredBonuses, closePopupHandler]);

  return (
    filteredBonuses.length !== 0 && (
      <BasePopup className="registration-bonus-popup">
        <HeaderTitle className="center block-lg size-xl" title="registration_bonus_popup_header">
          <div className="header-title__children">
            <button onClick={closePopupHandler} type="button">
              <Icon name="close" />
            </button>
          </div>
        </HeaderTitle>

        <BonusesWrapper
          bonuses={filteredBonuses}
          renderBonus={bonus => (
            <BonusCardWrapper
              activateBonus={activateBonusHandler}
              bonus={bonus}
              currency={wallet.currency}
              key={bonus.name}
            />
          )}
        />
      </BasePopup>
    )
  );
};

RegistrationBonusPopup.propTypes = propTypes;

export default RegistrationBonusPopup;
